/** Card image files as the bulk queue receives them: pairing fronts with backs, reading them for the API. */

/** `card_A.jpg` → `{ base: "card", side: "A" }`; no suffix → side null. */
function splitSide(name: string): { base: string; side: "A" | "B" | null } {
  const dot = name.lastIndexOf(".");
  const stem = dot === -1 ? name : name.slice(0, dot);
  const suffix = stem.slice(-2).toUpperCase();
  if (suffix === "_A") return { base: stem.slice(0, -2), side: "A" };
  if (suffix === "_B") return { base: stem.slice(0, -2), side: "B" };
  return { base: stem, side: null };
}

export type CardImages = { front: File; back: File | null };

/**
 * Pairs `<name>_A` with `<name>_B` as the front and back of one card, matching
 * the convention hobby's bulk page uses (case-insensitive).
 *
 * A `_B` with no matching `_A` is not dropped: it becomes a front-only card,
 * since a lone back is more likely a naming slip than something to ignore.
 */
export function pairFiles(files: readonly File[]): CardImages[] {
  const backs = new Map<string, File>();
  for (const file of files) {
    const { base, side } = splitSide(file.name);
    if (side === "B") backs.set(base.toLowerCase(), file);
  }

  const cards: CardImages[] = [];

  for (const file of files) {
    const { base, side } = splitSide(file.name);
    const key = base.toLowerCase();

    if (side === "B") {
      const hasFront = files.some((f) => {
        const other = splitSide(f.name);
        return other.side === "A" && other.base.toLowerCase() === key;
      });
      if (hasFront) continue;
      cards.push({ front: file, back: null });
      continue;
    }

    cards.push({ front: file, back: side === "A" ? (backs.get(key) ?? null) : null });
  }

  return cards;
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
